import {
    PLAIN_SUBJECT_PREFIX,
    PLAIN_TEXT_AFTER,
    PLAIN_TEXT_BEFORE,
    extractEmailBodyInnerHtml,
    plainAfterToHtml,
    plainAfterToHtmlForPreview,
    plainBeforeToHtml,
    plainBeforeToHtmlForPreview,
    subjectPlainToTemplate,
} from "./inactiveOrderReminderMailPlainText";

/** Тег для подстановки названия дилерской (FreeMarker). */
export const INACTIVE_ORDER_REMINDER_DEALERSHIP_TAG = "${dealershipName}";

const HTML_OPEN = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Order reminder</title>
    <style>
        body { margin: 0; padding: 16px; font-family: Arial, sans-serif; font-size: 15px; line-height: 1.5; color: #333333; }
        a { color: #1a73e8; text-decoration: none; }
        a:hover { text-decoration: underline; }
        ul { padding-left: 20px; }
        li { margin-bottom: 8px; }
        .muted { color: #666666; font-size: 13px; }
    </style>
</head>
<body>`;

const HTML_CLOSE = `</body></html>`;

const PRODUCTS_LIST_BLOCK = `<ul>
    <#list products as product>
        <li>
            <#if product.url?? && product.url?has_content>
                <a href="\${origin!''}/product/\${product.url}" target="_blank">\${product.productName!''}</a>
            <#else>
                \${product.productName!''}
            </#if>
            <span class="muted"> — last order: \${product.lastOrderDate!''}<#if product.inactiveTier??> (\${product.inactiveTier}+ months)</#if></span>
        </li>
    </#list>
</ul>`;

export {
    PLAIN_SUBJECT_PREFIX as DEFAULT_INACTIVE_ORDER_REMINDER_SUBJECT,
    PLAIN_TEXT_BEFORE as DEFAULT_TEXT_BEFORE_PRODUCTS,
    PLAIN_TEXT_AFTER as DEFAULT_TEXT_AFTER_PRODUCTS,
    withInactiveOrderReminderPlainDefaults as withInactiveOrderReminderMailDefaults,
} from "./inactiveOrderReminderMailPlainText";

const COMPANY_FOOTER_HTML =
    '<p class="muted">${companyName!\'\'} | ${companyPhone!\'\'} | <a href="mailto:${companyEmail!\'\'}">${companyEmail!\'\'}</a></p>';

export function composeInactiveOrderReminderEmailBody(textBeforeHtml: string, textAfterHtml: string): string {
    const before = textBeforeHtml.trim();
    const after = textAfterHtml.trim();
    const afterBlock = after.includes('class="muted"') ? after : `${after}${after ? "\n" : ""}${COMPANY_FOOTER_HTML}`;
    return `${HTML_OPEN}${before}${PRODUCTS_LIST_BLOCK}${afterBlock}${HTML_CLOSE}`;
}

/** Полный HTML из текстовых полей формы. */
export function buildComposedEmailHtmlFromFormFields(
    textBeforePlain: string,
    textAfterPlain: string,
): string {
    return composeInactiveOrderReminderEmailBody(
        plainBeforeToHtml(textBeforePlain),
        plainAfterToHtml(textAfterPlain),
    );
}

export type InactiveOrderReminderPreviewSample = {
    dealershipName: string;
    origin: string;
    companyName: string;
    companyPhone: string;
    companyEmail: string;
};

const SAMPLE_PRODUCTS = [
    {
        productName: "Self-Locking Key Tags - White",
        url: "1000873-self-locking-key-tags-white",
        lastOrderDate: "01.15.2025",
        inactiveTier: 6,
    },
    {
        productName: "Exterior - Addendum - Custom",
        url: "1001914-exterior-addendum-custom",
        lastOrderDate: "12.01.2024",
        inactiveTier: 9,
    },
];

export function defaultInactiveOrderReminderPreviewSample(): InactiveOrderReminderPreviewSample {
    return {
        dealershipName: "Sample Dealership LLC",
        origin: typeof window !== "undefined" ? window.location.origin : "https://akdealerservices.com",
        companyName: "AK Dealer Services",
        companyPhone: "(555) 123-4567",
        companyEmail: "info@akdealerservices.com",
    };
}

function replaceScalarVars(template: string, sample: InactiveOrderReminderPreviewSample): string {
    return template
        .replace(/\$\{dealershipName!''\}/g, sample.dealershipName)
        .replace(/\$\{dealershipName\}/g, sample.dealershipName)
        .replace(/\$\{origin!''\}/g, sample.origin)
        .replace(/\$\{origin\}/g, sample.origin)
        .replace(/\$\{companyName!''\}/g, sample.companyName)
        .replace(/\$\{companyName\}/g, sample.companyName)
        .replace(/\$\{companyPhone!''\}/g, sample.companyPhone)
        .replace(/\$\{companyPhone\}/g, sample.companyPhone)
        .replace(/\$\{companyEmail!''\}/g, sample.companyEmail)
        .replace(/\$\{companyEmail\}/g, sample.companyEmail);
}

function renderProductFragment(fragment: string, product: (typeof SAMPLE_PRODUCTS)[0], sample: InactiveOrderReminderPreviewSample): string {
    let html = fragment;
    const urlIf = /<#if\s+product\.url\?\?\s*&&\s*product\.url\?has_content>([\s\S]*?)<#else>([\s\S]*?)<\/#if>/i;
    const urlMatch = html.match(urlIf);
    if (urlMatch) {
        const branch = product.url?.trim() ? urlMatch[1] : urlMatch[2];
        html = html.replace(urlIf, branch);
    }

    const tierIf = /<#if\s+product\.inactiveTier\?\?>([\s\S]*?)<\/#if>/gi;
    html = html.replace(tierIf, (_, inner) => (product.inactiveTier != null ? inner : ""));

    html = html
        .replace(/\$\{product\.productName!''\}/g, product.productName)
        .replace(/\$\{product\.productName\}/g, product.productName)
        .replace(/\$\{product\.lastOrderDate!''\}/g, product.lastOrderDate)
        .replace(/\$\{product\.lastOrderDate\}/g, product.lastOrderDate)
        .replace(/\$\{product\.inactiveTier\}/g, String(product.inactiveTier ?? ""))
        .replace(/\$\{product\.url\}/g, product.url ?? "");

    return replaceScalarVars(html, sample);
}

function expandProductList(template: string, sample: InactiveOrderReminderPreviewSample): string {
    const listRe = /<#list\s+products\s+as\s+product>([\s\S]*?)<\/#list>/i;
    const match = template.match(listRe);
    if (!match) {
        return template;
    }
    const inner = match[1];
    const itemsHtml = SAMPLE_PRODUCTS.map((p) => renderProductFragment(inner, p, sample)).join("");
    return template.replace(listRe, itemsHtml);
}

function stripRemainingFreeMarker(template: string): string {
    return template.replace(/<#[\s\S]*?>/g, "").replace(/<\/#[\s\S]*?>/g, "");
}

/** Стили превью (Shadow DOM). */
export const PREVIEW_EMAIL_CSS =
    ".email-preview-body{margin:0;padding:16px;font-family:Arial,sans-serif;font-size:15px;line-height:1.5;color:#333333}" +
    ".email-preview-body a{color:#1a73e8;text-decoration:none}" +
    ".email-preview-body a:hover{text-decoration:underline}" +
    ".email-preview-body ul{padding-left:20px;margin:0.35em 0}" +
    ".email-preview-body li{margin-bottom:8px}" +
    ".email-preview-body p{margin:0}" +
    ".email-preview-body p:not(:last-child){margin-bottom:0.75em}" +
    ".email-preview-body .muted{color:#666666;font-size:13px}";

/** Только HTML-содержимое письма для превью (без head/style как текст). */
export function buildEmailPreviewBodyHtml(html: string): string {
    const trimmed = html.trim();
    if (!trimmed) {
        return "";
    }
    return stripRemainingFreeMarker(extractEmailBodyInnerHtml(trimmed));
}

/** textBefore / textAfter — простой текст из формы (без HTML). */
export function previewInactiveOrderReminderHtmlFromParts(
    textBeforePlain: string,
    textAfterPlain: string,
    sample: InactiveOrderReminderPreviewSample,
): string {
    return previewInactiveOrderReminderHtml(
        composeInactiveOrderReminderEmailBody(
            plainBeforeToHtmlForPreview(textBeforePlain),
            plainAfterToHtmlForPreview(textAfterPlain),
        ),
        sample,
    );
}

export function previewInactiveOrderReminderSubjectFromPlain(
    subjectPlain: string,
    sample: InactiveOrderReminderPreviewSample,
): string {
    return previewInactiveOrderReminderSubject(subjectPlainToTemplate(subjectPlain), sample);
}

/** Приблизительный рендер HTML-шаблона для превью (не полный FreeMarker). */
export function previewInactiveOrderReminderHtml(
    bodyTemplate: string,
    sample: InactiveOrderReminderPreviewSample,
): string {
    if (!bodyTemplate.trim()) {
        return "";
    }
    let html = expandProductList(bodyTemplate, sample);
    html = replaceScalarVars(html, sample);
    return buildEmailPreviewBodyHtml(html);
}

export function previewInactiveOrderReminderSubject(
    subjectTemplate: string,
    sample: InactiveOrderReminderPreviewSample,
): string {
    if (!subjectTemplate.trim()) {
        return "";
    }
    return replaceScalarVars(stripRemainingFreeMarker(subjectTemplate), sample).trim();
}

export function insertTextAtSelection(
    value: string,
    insertion: string,
    selectionStart: number,
    selectionEnd: number,
): { nextValue: string; cursor: number } {
    const nextValue = value.slice(0, selectionStart) + insertion + value.slice(selectionEnd);
    const cursor = selectionStart + insertion.length;
    return {nextValue, cursor};
}
